import React, { useEffect, useMemo, useState } from "react";
import { api, extractError } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Plus, Search, ChevronDown, ChevronRight } from "lucide-react";
import { policyDisplayName } from "@/lib/policyDisplay";
import { formatDateIt } from "@/lib/format";

const TYPES = [
    { key: "PA", label: "PA · Politica aziendale" },
    { key: "BT", label: "BT · Bollettino tecnico" },
];

export default function PolicyList() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [series, setSeries] = useState([]);
    const [q, setQ] = useState("");
    const [fType, setFType] = useState("");
    const [fSeries, setFSeries] = useState("");
    const [order, setOrder] = useState("asc"); // asc = oldest first (default)
    const [expandDrafts, setExpandDrafts] = useState(false);
    const [creating, setCreating] = useState(false);

    const load = async () => {
        const params = { order };
        if (fType) params.type = fType;
        if (fSeries) params.series = fSeries;
        if (q.trim()) params.q = q.trim();
        const [{ data }, s] = await Promise.all([
            api.get("/policies", { params }),
            api.get("/policies/series"),
        ]);
        setItems(data); setSeries(s.data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [fType, fSeries, order]);
    useEffect(() => {
        const t = setTimeout(load, 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [q]);

    const isAdmin = user?.role === "admin";
    const { published, drafts } = useMemo(() => {
        const pub = []; const drf = [];
        items.forEach((p) => (p.status === "draft" ? drf : pub).push(p));
        return { published: pub, drafts: drf };
    }, [items]);

    return (
        <div className="p-6" data-testid="policy-list-page">
            <div className="flex items-baseline justify-between mb-4">
                <div>
                    <h1 className="font-display text-2xl font-semibold tracking-tight text-neutral-900">Policy</h1>
                    <div className="text-sm text-neutral-500">
                        Politiche aziendali &amp; bollettini tecnici · {published.length} pubblicate
                        {drafts.length > 0 && ` · ${drafts.length} bozze`}
                    </div>
                </div>
                {isAdmin && (
                    <button onClick={() => setCreating(true)} data-testid="new-policy-btn"
                        className="inline-flex items-center gap-1.5 bg-neutral-900 text-white text-sm font-medium px-3 py-1.5 rounded-md hover:bg-neutral-800">
                        <Plus size={14} /> Nuova policy
                    </button>
                )}
            </div>

            <div className="bg-white border border-neutral-200 rounded-md p-3 flex flex-wrap gap-2 mb-4">
                <div className="relative flex-1 min-w-[220px]">
                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <input value={q} onChange={(e) => setQ(e.target.value)}
                        data-testid="policy-search"
                        placeholder="Cerca titolo o testo…"
                        className="w-full pl-7 pr-3 py-1.5 text-sm border border-neutral-200 rounded" />
                </div>
                <select value={fType} onChange={(e) => setFType(e.target.value)} data-testid="filter-type"
                    className="text-sm border border-neutral-200 rounded px-2 py-1.5">
                    <option value="">Tutti i tipi</option>
                    {TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
                <select value={fSeries} onChange={(e) => setFSeries(e.target.value)} data-testid="filter-series"
                    className="text-sm border border-neutral-200 rounded px-2 py-1.5">
                    <option value="">Tutti gli argomenti</option>
                    {series.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={order} onChange={(e) => setOrder(e.target.value)} data-testid="filter-order"
                    className="text-sm border border-neutral-200 rounded px-2 py-1.5">
                    <option value="asc">Più vecchie prima</option>
                    <option value="desc">Più recenti prima</option>
                </select>
            </div>

            {/* Published policies (main list) */}
            <PolicyRows items={published} onOpen={(p) => navigate(`/policy/${p.id}`)} testId="policy-published" emptyText="Nessuna policy pubblicata." />

            {/* Drafts in an expandable accordion, mirroring InternalProjects "In pausa"/"Concluso" */}
            <div className="mt-3">
                <div className="bg-neutral-100/60 border border-dashed border-neutral-200 rounded-md overflow-hidden" data-testid="policy-drafts-folder">
                    <button onClick={() => setExpandDrafts(!expandDrafts)}
                        data-testid="policy-drafts-toggle"
                        className="w-full flex items-center gap-2 px-4 py-2 hover:bg-neutral-100">
                        {expandDrafts ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        <span className="font-display text-xs font-medium tracking-wide uppercase text-neutral-500">
                            Bozze · {drafts.length}
                        </span>
                    </button>
                    {expandDrafts && (
                        <div className="opacity-70">
                            <PolicyRows items={drafts} onOpen={(p) => navigate(`/policy/${p.id}`)} testId="policy-drafts" emptyText="Nessuna bozza." muted />
                        </div>
                    )}
                </div>
            </div>

            {creating && (
                <NewPolicyModal
                    seriesList={series}
                    onClose={() => setCreating(false)}
                    onCreated={(pol) => { setCreating(false); navigate(`/policy/${pol.id}`); }}
                />
            )}
        </div>
    );
}

function PolicyRows({ items, onOpen, testId, emptyText, muted }) {
    if (items.length === 0) {
        return (
            <div className={`bg-white border ${muted ? "" : "border-neutral-200 rounded-md"} p-6 text-sm text-neutral-400`}
                data-testid={`${testId}-empty`}>
                {emptyText}
            </div>
        );
    }
    return (
        <div className={`${muted ? "" : "bg-white border border-neutral-200 rounded-md"} divide-y divide-neutral-100`} data-testid={testId}>
            {items.map((p) => (
                <PolicyRow key={p.id} p={p} onClick={() => onOpen(p)} />
            ))}
        </div>
    );
}

function PolicyRow({ p, onClick }) {
    const isDraft = p.status === "draft";
    return (
        <button onClick={onClick} data-testid={`policy-row-${p.id}`}
            className="w-full text-left px-4 py-3 hover:bg-neutral-50 flex items-start gap-3">
            <span className={`shrink-0 mt-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded ${p.type === "PA" ? "bg-sky-50 text-sky-700 border border-sky-200" : "bg-violet-50 text-violet-700 border border-violet-200"}`}>
                {p.type}
            </span>
            <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-neutral-900 truncate">
                    {policyDisplayName(p)}
                    {isDraft && <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">Bozza</span>}
                </div>
                <div className="text-xs text-neutral-500 truncate mt-0.5">
                    {p.series ? <>Serie: <span className="text-neutral-700">{p.series}</span>{p.series_number ? ` · N. ${p.series_number}` : ""}</> : "—"}
                    {p.published_at && <> · Pubblicata il {formatDateIt(p.published_at)}</>}
                    {p.last_modified_at && (p.revision > 0) && <> · Ultima modifica {formatDateIt(p.last_modified_at)}</>}
                </div>
            </div>
        </button>
    );
}

function NewPolicyModal({ seriesList, onClose, onCreated }) {
    const [type, setType] = useState("PA");
    const [title, setTitle] = useState("");
    const [seriesInput, setSeriesInput] = useState("");
    const [publishNow, setPublishNow] = useState(false);
    const [backdate, setBackdate] = useState("");
    const [busy, setBusy] = useState(false); const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault(); setBusy(true); setErr("");
        try {
            const payload = {
                type, title: title.trim(),
                series: seriesInput.trim() || null,
                status: publishNow ? "published" : "draft",
                published_at: publishNow && backdate ? backdate : null,
            };
            const { data } = await api.post("/policies", payload);
            onCreated(data);
        } catch (e) { setErr(extractError(e)); } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 bg-neutral-900/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
                data-testid="new-policy-modal"
                className="bg-white rounded-md shadow-lg w-full max-w-lg">
                <div className="px-5 py-3 border-b border-neutral-200 font-display font-semibold">Nuova policy</div>
                <div className="p-5 grid grid-cols-2 gap-3 text-sm">
                    <label className="col-span-2 block">
                        <span className="text-xs font-medium text-neutral-600">Titolo*</span>
                        <input required value={title} onChange={(e) => setTitle(e.target.value)}
                            data-testid="new-policy-title"
                            className="mt-1 w-full px-2 py-1.5 border border-neutral-300 rounded" />
                    </label>
                    <label className="block">
                        <span className="text-xs font-medium text-neutral-600">Tipo</span>
                        <select value={type} onChange={(e) => setType(e.target.value)}
                            data-testid="new-policy-type"
                            className="mt-1 w-full px-2 py-1.5 border border-neutral-300 rounded">
                            <option value="PA">PA · Politica aziendale</option>
                            <option value="BT">BT · Bollettino tecnico</option>
                        </select>
                    </label>
                    <label className="block">
                        <span className="text-xs font-medium text-neutral-600">Argomento / Serie</span>
                        <input list="policy-series-list" value={seriesInput} onChange={(e) => setSeriesInput(e.target.value)}
                            data-testid="new-policy-series" placeholder="es. Comunicazione interna"
                            className="mt-1 w-full px-2 py-1.5 border border-neutral-300 rounded" />
                        <datalist id="policy-series-list">
                            {seriesList.map((s) => <option key={s} value={s} />)}
                        </datalist>
                    </label>
                    <label className="col-span-2 flex items-center gap-2 pt-1">
                        <input type="checkbox" checked={publishNow} onChange={(e) => setPublishNow(e.target.checked)}
                            data-testid="new-policy-publish-toggle" />
                        <span className="text-xs">Pubblica subito (altrimenti viene salvata come bozza)</span>
                    </label>
                    {publishNow && (
                        <label className="col-span-2 block">
                            <span className="text-xs font-medium text-neutral-600">Data di pubblicazione (facoltativa, per retrodatare)</span>
                            <input type="date" value={backdate} onChange={(e) => setBackdate(e.target.value)}
                                data-testid="new-policy-backdate"
                                className="mt-1 w-full px-2 py-1.5 border border-neutral-300 rounded" />
                            <div className="text-[11px] text-neutral-500 mt-1">Vuoto = oggi</div>
                        </label>
                    )}
                </div>
                {err && <div className="mx-5 mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}
                <div className="px-5 py-3 border-t border-neutral-100 flex justify-end gap-2 bg-neutral-50">
                    <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-neutral-300 bg-white">Annulla</button>
                    <button type="submit" disabled={busy || !title.trim()} data-testid="save-new-policy"
                        className="px-3 py-1.5 text-sm rounded bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-60">
                        {busy ? "Creazione…" : publishNow ? "Crea e pubblica" : "Salva bozza"}
                    </button>
                </div>
            </form>
        </div>
    );
}
